import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCRDetail, validateCR, rejectCR, resendEmail, resetCRPending, regeneratePDF,
         getPiecesCR, setPiecesCR, getLignesDevis, setLignesDevis } from '../services/api';
import { useAuth } from '../App';

// ── Helpers ───────────────────────────────────────────────────────────────────
const token = () => localStorage.getItem('admin_token');

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <span style={{ color: 'var(--text2)', flexShrink: 0, marginRight: 12 }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function PhotoGrid({ photos, label }) {
  if (!photos || !Array.isArray(photos) || photos.length === 0) return null;
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {photos.map((p, i) => (
          <img key={i} src={p} alt="" style={{ width: 180, height: 135, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }}
            onClick={() => window.open(p, '_blank')} />
        ))}
      </div>
    </div>
  );
}

function CommentBox({ label, value, color }) {
  if (!value) return null;
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, lineHeight: 1.5, background: color || 'var(--bg3)', borderRadius: 6, padding: '8px 12px' }}>{value}</div>
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
const TYPES_ECHEC = [
  'Client absent', 'Problème d’accès', 'Cheminement introuvable',
  'Chambre télécom inaccessible (tampon scellé)', 'Blocage GC (entre deux chambres)',
  'Intervention sur DEVIS', 'RDVNH (rendez-vous non honoré faute technicien)',
  'Client non éligible FTTH', 'Commande annulé par le client',
  'Chambre sur voirie besoin ARC', 'Pas de continuité PB PM', 'Pas de signal PM',
  'PBO Saturé', 'Erreur d’adresse', 'Travaux à réaliser par le client',
  'Report RDV suite demande client ou GEM', 'Indisponibilité technicien ou binôme',
  'Fourreau bouché / hydrocurage nécessaire', 'Autorisation propriétaire ou syndic',
  'Autre',
];

function EditModal({ cr, onClose, onSave }) {
  const [form, setForm] = useState({
    resultat: cr.resultat || '',
    commentaire_intervention: cr.commentaire_intervention || cr.commentaire_technicien || cr.commentaire_intervenant || '',
    nom_contact: cr.nom_contact || '',
    no_decharge: cr.no_decharge || cr.decharge_kosc || '',
    ref_commande: cr.ref_commande || '',
    ref_work_order: cr.ref_work_order || '',
    numero_serie_ont: cr.numero_serie_ont || '',
    type_raccordement: cr.type_raccordement || '',
    forfait_admin_override: cr.forfait_admin_override || '',
    lg_public: cr.lg_public || '',
    lg_prive: cr.lg_prive || '',
    lg_faux_plafond: cr.lg_faux_plafond || '',
    type_echec: cr.type_echec || '',
    type_echec_autre: cr.type_echec_autre || '',
    commentaire_blockage: cr.commentaire_blockage || '',
    emplacement_point_bloquant: cr.emplacement_point_bloquant || '',
    dist_a_bloquant: cr.dist_a_bloquant || '',
    dist_b_bloquant: cr.dist_b_bloquant || '',
    nb_conduites: cr.nb_conduites || '',
    nb_conduites_cassees: cr.nb_conduites_cassees || '',
    nb_conduites_saturees: cr.nb_conduites_saturees || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...form,
      lg_total: ((parseFloat(form.lg_public) || 0) + (parseFloat(form.lg_prive) || 0)) || '',
    };
    try { await onSave(payload); onClose(); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div className="modal-title">Modifier le compte-rendu</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--blue2)' }}>
            ℹ️ Seules les informations textuelles sont modifiables ici. Les photos ne peuvent pas être modifiées.
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Réf commande</label>
              <input className="form-input" value={form.ref_commande} onChange={e => setForm(f => ({ ...f, ref_commande: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Réf Work Order</label>
              <input className="form-input" value={form.ref_work_order} onChange={e => setForm(f => ({ ...f, ref_work_order: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Résultat</label>
            <select className="form-input form-select" value={form.resultat} onChange={e => setForm(f => ({ ...f, resultat: e.target.value }))}>
              <option value="">— Sélectionner —</option>
              <option value="CLEARED">CLEARED</option>
              <option value="FAILED">FAILED</option>
            </select>
          </div>
          {form.resultat === 'FAILED' && (
            <>
              <div className="form-group">
                <label className="form-label">Type d’échec</label>
                <select className="form-input form-select" value={form.type_echec}
                  onChange={e => setForm(f => ({ ...f, type_echec: e.target.value }))}>
                  <option value="">— Sélectionner —</option>
                  {TYPES_ECHEC.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.45 }}>
                  C’est ce motif qui part sur le CRI à Covage et qui décide de la suite du
                  dossier. Le PDF est regénéré à l’enregistrement.
                </div>
              </div>
              {form.type_echec === 'Autre' && (
                <div className="form-group">
                  <label className="form-label">Précision</label>
                  <input className="form-input" value={form.type_echec_autre}
                    onChange={e => setForm(f => ({ ...f, type_echec_autre: e.target.value }))} />
                </div>
              )}
              {form.type_echec === 'Blocage GC (entre deux chambres)' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Commentaire du blocage</label>
                    <textarea className="form-input" rows={2} value={form.commentaire_blockage}
                      onChange={e => setForm(f => ({ ...f, commentaire_blockage: e.target.value }))} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Chambre A → point bloquant (ml)</label>
                      <input className="form-input" type="number" min="0" value={form.dist_a_bloquant}
                        onChange={e => setForm(f => ({ ...f, dist_a_bloquant: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Chambre B → point bloquant (ml)</label>
                      <input className="form-input" type="number" min="0" value={form.dist_b_bloquant}
                        onChange={e => setForm(f => ({ ...f, dist_b_bloquant: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Conduites</label>
                      <input className="form-input" type="number" min="0" value={form.nb_conduites}
                        onChange={e => setForm(f => ({ ...f, nb_conduites: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Cassées</label>
                      <input className="form-input" type="number" min="0" value={form.nb_conduites_cassees}
                        onChange={e => setForm(f => ({ ...f, nb_conduites_cassees: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Saturées</label>
                      <input className="form-input" type="number" min="0" value={form.nb_conduites_saturees}
                        onChange={e => setForm(f => ({ ...f, nb_conduites_saturees: e.target.value }))} />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type de raccordement réalisé</label>
              <select className="form-input form-select" value={form.type_raccordement} onChange={e => setForm(f => ({ ...f, type_raccordement: e.target.value }))}>
                <option value="">— Sélectionner —</option>
                <option value="RAC_I">RAC_I</option>
                <option value="RAC_C">RAC_C</option>
                <option value="RAC_P">RAC_P</option>
                <option value="RAC_F">RAC_F</option>
                <option value="PTO_C">PTO_C</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">N° série ONT</label>
              <input className="form-input" value={form.numero_serie_ont} onChange={e => setForm(f => ({ ...f, numero_serie_ont: e.target.value }))} />
            </div>
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, margin: '12px 0 6px', color: 'var(--text2)' }}>Longueurs câble</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Domaine public (ml)</label>
              <input className="form-input" type="number" min="0" step="0.1" value={form.lg_public} onChange={e => setForm(f => ({ ...f, lg_public: e.target.value }))} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="form-label">Domaine privé (ml)</label>
              <input className="form-input" type="number" min="0" step="0.1" value={form.lg_prive} onChange={e => setForm(f => ({ ...f, lg_prive: e.target.value }))} placeholder="0" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Faux plafond (ml)</label>
              <input className="form-input" type="number" min="0" step="0.1" value={form.lg_faux_plafond} onChange={e => setForm(f => ({ ...f, lg_faux_plafond: e.target.value }))} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="form-label">Total cablage (ml) — auto</label>
              <input className="form-input" value={((parseFloat(form.lg_public) || 0) + (parseFloat(form.lg_prive) || 0)) || ''} readOnly style={{ background: 'var(--bg3)', color: 'var(--text3)', cursor: 'default' }} placeholder="calculé auto" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Forfait TVX (email)</label>
            <select className="form-input form-select" value={form.forfait_admin_override} onChange={e => setForm(f => ({ ...f, forfait_admin_override: e.target.value }))}>
              <option value="">— Laisser calculé automatiquement —</option>
              <option value="Aucun">Aucun</option>
              <option value="Forfait 1">Forfait 1</option>
              <option value="DEVIS">DEVIS</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Nom contact sur site</label>
              <input className="form-input" value={form.nom_contact} onChange={e => setForm(f => ({ ...f, nom_contact: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">N° décharge FRONT COVAGE</label>
              <input className="form-input" value={form.no_decharge} onChange={e => setForm(f => ({ ...f, no_decharge: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Commentaire / Travaux réalisés</label>
            <textarea className="form-input" value={form.commentaire_intervention} onChange={e => setForm(f => ({ ...f, commentaire_intervention: e.target.value }))} rows={4} style={{ resize: 'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '...' : '💾 Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Email Preview Modal ───────────────────────────────────────────────────────
/**
 * Les documents qui partiront avec le compte rendu, et le devis qu'on peut
 * corriger avant.
 *
 * Un compte rendu portant un devis ne partait qu'en interne : la conduite
 * d'activité ouvrait le fichier, corrigeait à la main, et l'envoyait elle-même
 * à Covage. Elle corrige ici, et la validation part directement — c'est cette
 * relecture qui remplace le détour.
 *
 * Décocher un document le retire de l'envoi. Décocher le devis garde en outre
 * le mail en interne : envoyer à Covage un compte rendu qui annonce un devis
 * sans le joindre n'aurait pas de sens.
 */
function DocumentsJoints({ crId, onChange }) {
  const [pieces, setPieces] = useState(null);
  const [interne, setInterne] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [lignes, setLignes] = useState(null);
  const [saisie, setSaisie] = useState({});
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    getPiecesCR(crId)
      .then(r => { setPieces(r.pieces); setInterne(!!r.envoi_interne); })
      .catch(() => setPieces([]));
  }, [crId]);

  const enregistrer = async (listePieces, envoiInterne) => {
    setErreur(null);
    try {
      await setPiecesCR(crId, listePieces.filter(p => !p.jointe).map(p => p.cle), envoiInterne);
      if (onChange) onChange();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    }
  };

  const basculer = (cle) => {
    const suivant = pieces.map(p => (p.cle === cle ? { ...p, jointe: !p.jointe } : p));
    setPieces(suivant);
    enregistrer(suivant, interne);
  };

  const basculerInterne = () => {
    const vers = !interne;
    setInterne(vers);
    enregistrer(pieces || [], vers);
  };

  const ouvrirDevis = async () => {
    setErreur(null); setMsg(null);
    try {
      const r = await getLignesDevis(crId);
      setLignes(r);
      const s = {};
      r.lignes.forEach(l => { s[l.cellule] = l.quantite === null ? '' : String(l.quantite); });
      setSaisie(s);
    } catch (e) { setErreur(e.response?.data?.error || 'Lecture du devis impossible'); }
  };

  const enregistrerDevis = async () => {
    setEnCours(true); setErreur(null); setMsg(null);
    try {
      const q = {};
      Object.entries(saisie).forEach(([c, v]) => { q[c] = v === '' ? null : Number(v); });
      const r = await setLignesDevis(crId, q);
      setLignes(r);
      setMsg('Devis enregistré — nouveau total ' + r.total + ' €');
      if (onChange) onChange();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    } finally { setEnCours(false); }
  };

  // Le total se recalcule à l'écran pendant la saisie : attendre
  // l'enregistrement pour voir le montant obligerait à enregistrer pour juger.
  const totalEnCours = lignes
    ? lignes.lignes.reduce((s, l) => s + (Number(saisie[l.cellule]) || 0) * (l.prix || 0), 0)
    : 0;

  if (!pieces) return null;

  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10,
                  padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        {pieces.length ? 'Documents joints' : 'Envoi'}
      </div>
      {erreur && <div className="alert alert-error" style={{ marginBottom: 8 }}>{erreur}</div>}

      {/* Garder un compte rendu en interne est une décision, pas un effet de
          bord : on la coche, et le bandeau des destinataires le montre. */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer',
                      padding: '6px 0', marginBottom: pieces.length ? 6 : 0,
                      borderBottom: pieces.length ? '1px solid var(--border)' : 'none' }}>
        <input type="checkbox" checked={interne} onChange={basculerInterne}
          style={{ width: 16, height: 16, marginTop: 2 }} />
        <span>
          <span style={{ fontSize: 12.5, fontWeight: 600, display: 'block',
                         color: interne ? '#b45309' : 'var(--text)' }}>
            Ne pas envoyer à Covage — garder en interne
          </span>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
            Le compte rendu est validé et le mail ne part qu'à cri@valeatelecom.com.
          </span>
        </span>
      </label>

      {pieces.map(p => (
        <div key={p.cle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
            <input type="checkbox" checked={p.jointe} onChange={() => basculer(p.cle)}
              style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 12.5, fontWeight: p.jointe ? 600 : 400,
                           color: p.jointe ? 'var(--text)' : 'var(--text3)',
                           textDecoration: p.jointe ? 'none' : 'line-through' }}>
              {p.libelle}
            </span>
            {!p.existe && <span style={{ fontSize: 11, color: '#b91c1c' }}>fichier introuvable</span>}
          </label>
          {p.editable && p.existe && (
            <button className="btn btn-secondary btn-sm" onClick={ouvrirDevis}>Modifier</button>
          )}
        </div>
      ))}

      {lignes && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
            Quantités du devis — vider une case retire la ligne.
          </div>
          <div style={{ maxHeight: '32vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <tbody>
                {lignes.lignes.map(l => {
                  const q = Number(saisie[l.cellule]) || 0;
                  return (
                    <tr key={l.cellule} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '3px 6px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>{l.ref}</td>
                      <td style={{ padding: '3px 6px' }}>{l.libelle}</td>
                      <td style={{ padding: '3px 6px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {l.prix} € / {l.unite}
                      </td>
                      <td style={{ padding: '3px 6px', width: 80 }}>
                        <input type="number" min="0" step="0.01" value={saisie[l.cellule] ?? ''}
                          onChange={e => setSaisie(s => ({ ...s, [l.cellule]: e.target.value }))}
                          style={{ width: '100%', fontSize: 11.5, padding: '3px 6px', borderRadius: 5,
                                   border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }} />
                      </td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap',
                                   color: q ? 'var(--text)' : 'var(--text3)', fontWeight: q ? 600 : 400 }}>
                        {q ? (q * (l.prix || 0)).toFixed(2) + ' €' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>Total : {totalEnCours.toFixed(2)} €</strong>
            <button className="btn btn-primary btn-sm" onClick={enregistrerDevis} disabled={enCours}>
              {enCours ? 'Enregistrement…' : 'Enregistrer le devis'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setLignes(null)}>Fermer</button>
            {msg && <span style={{ fontSize: 11.5, color: 'var(--green2)', fontWeight: 600 }}>{msg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function EmailPreviewModal({ crId, cr, onClose, onConfirm }) {
  const [forfait, setForfait] = useState(() => {
    if (cr.forfait_admin_override) return cr.forfait_admin_override;
    const zoho = cr.zoho_record || {};
    if (zoho.Devis_forfaitaire === true || zoho.Devis_forfaitaire === 'true') return 'DEVIS';
    if (cr.fpv === true) return 'Forfait 1';
    return 'Aucun';
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const loadPreview = async (f) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/cr/${crId}/email-preview?forfait=${encodeURIComponent(f)}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      setPreview(data);
    } catch { setPreview(null); }
    setLoading(false);
  };

  useEffect(() => { loadPreview(forfait); }, [forfait]);

  const handleConfirm = async () => {
    setSending(true); setError('');
    try {
      await onConfirm(forfait);
      onClose();
    } catch(e) {
      setError(e.message || 'Erreur lors de la validation');
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720, width: '95vw' }}>
        <div className="modal-header">
          <div className="modal-title">Preview email — {preview?.subject || '...'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

          {/* Forfait TVX selector */}
          <div style={{ background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--blue2)' }}>
              ✏️ FORFAIT TVX REALISE CHEZ LE CLIENT
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['Aucun', 'Forfait 1', 'DEVIS'].map(opt => (
                <button key={opt} onClick={() => setForfait(opt)}
                  style={{ padding: '7px 16px', borderRadius: 8, border: `2px solid ${forfait === opt ? 'var(--blue2)' : 'var(--border)'}`, background: forfait === opt ? 'rgba(37,99,235,0.12)' : 'var(--bg3)', color: forfait === opt ? 'var(--blue2)' : 'var(--text2)', fontWeight: forfait === opt ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <DocumentsJoints crId={crId} onChange={() => loadPreview(forfait)} />

          {/* Corps email */}
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>Corps du mail (texte brut) :</div>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center' }}><span className="spinner" /></div>
          ) : preview ? (
            <pre style={{ background: 'var(--bg3)', borderRadius: 8, padding: '14px 16px', fontSize: 12, lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word', border: '1px solid var(--border)', margin: 0, fontFamily: 'monospace', color: 'var(--text)', maxHeight: '45vh', overflowY: 'auto' }}>
              {preview.body}
            </pre>
          ) : (
            <div style={{ color: 'var(--text3)', padding: 16 }}>Impossible de charger le preview.</div>
          )}

          {/* Destinataires + PJ */}
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text3)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {preview?.to && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ color: 'var(--text3)' }}>Destinataires :</span>
                {preview.to.map((addr, i) => {
                  const isCov = ['service.delivery@covage.com','supplier-in-test@covage.com','supplier-in@covage.com','support-access@covage.com'].includes(addr);
                  return (
                    <span key={i} style={{ padding: '1px 8px', borderRadius: 12, fontSize: 11, fontWeight: isCov ? 700 : 400, background: isCov ? 'rgba(34,197,94,0.12)' : 'var(--bg3)', color: isCov ? '#22c55e' : 'var(--text2)', border: `1px solid ${isCov ? 'rgba(34,197,94,0.3)' : 'var(--border)'}` }}>
                      {addr}
                    </span>
                  );
                })}
              </div>
            )}
            <div>PJ : PDF{cr.devis_xlsx_url ? ' + Devis XLSX' : ''}{cr.dostec_gc_url ? ' + DOSTEC GC D15' : ''}{cr.dostec_gc_d14_url ? ' + DOSTEC GC D14' : ''}</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-success" onClick={handleConfirm} disabled={sending || loading}>
            {sending ? <span className="spinner" /> : '✓ Confirmer et envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CRDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const [cr, setCR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // 'validate-preview' | 'reject' | 'edit'
  const [motifRejet, setMotifRejet] = useState('');
  const [processing, setProcessing] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [replanif, setReplanif] = useState(false);

  useEffect(() => {
    getCRDetail(id).then(d => setCR(d.cr)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  // Le technicien a noté une nouvelle date lors d'un report : on l'applique
  // telle quelle à Zoho. Le technicien affecté n'est pas touché — seul le
  // rendez-vous bouge.
  const appliquerReport = async () => {
    const r = cr.report_rdv;
    if (!r || !r.date) return;
    setReplanif(true); setError(''); setMsg('');
    try {
      const res = await fetch(`/api/admin/planification/${cr.dossier_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          action: 'confirmer',
          dossier_ref: cr.ref_commande || cr.dossier_id,
          date_intervention: r.date,
          heure_intervention: r.heure || '',
          ref_work_order: cr.ref_work_order || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'échec');
      await fetch(`/api/admin/cr/${id}/report-applique`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` },
      });
      setCR(c => ({ ...c, report_applique: true }));
      setMsg('Intervention replanifiée au ' + r.date.split('-').reverse().join('/') + (r.heure ? ' à ' + r.heure : ''));
    } catch (e) {
      setError('Replanification impossible : ' + e.message);
    } finally { setReplanif(false); }
  };

  const handleValidate = async (forfait_override) => {
    const res = await fetch(`/api/admin/cr/${id}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ forfait_override })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erreur validation');
    setMsg(data.message || 'CR validé');
    setCR(c => ({ ...c, status: 'validated' }));
  };

  const handleReject = async () => {
    if (!motifRejet.trim()) { setError('Merci d\'indiquer un motif de rejet'); return; }
    setProcessing(true); setError('');
    try {
      await rejectCR(id, motifRejet);
      setMsg('CR rejeté — le technicien sera notifié');
      setCR(c => ({ ...c, status: 'rejected' }));
      setAction(null);
    } catch(e) { setError(e.response?.data?.error || 'Erreur'); }
    finally { setProcessing(false); }
  };

  const handleEdit = async (form) => {
    const res = await fetch(`/api/admin/cr/${id}/edit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify(form)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    setCR(c => ({ ...c, ...form }));
    setMsg('Modifications enregistrées');
  };

  if (loading) return <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><div className="spinner" /></div>;
  if (!cr) return <div className="page"><div className="empty"><div className="icon">📄</div><p>Compte-rendu introuvable</p></div></div>;

  const isPending = cr.status === 'pending';
  const isCRI = !cr.type_cr || cr.type_cr === 'CRI';
  const isFailed = cr.resultat === 'FAILED';
  const isCleared = cr.resultat === 'CLEARED';
  const typeColor = isCleared ? 'var(--green2)' : isFailed ? '#ff6b6b' : 'var(--blue2)';

  // Calcul forfait par défaut pour affichage dans le badge
  const zoho = cr.zoho_record || {};
  const forfaitDefault = cr.forfait_admin_override ||
    ((zoho.Devis_forfaitaire === true || zoho.Devis_forfaitaire === 'true') ? 'DEVIS' : (cr.fpv === true ? 'Forfait 1' : 'Aucun'));

  return (
    <>
      {/* ── Topbar ── */}
      <div className="topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/cr')}>← Retour</button>
          <div>
            <div className="topbar-title">{cr.ref_commande || cr.dossier_id}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>{cr.technicien_nom} · {cr.date_creation ? new Date(cr.date_creation).toLocaleString('fr-FR') : ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {cr.dossier_id && (
            <>
              {/* La fiche d'abord : c'est la notre, elle porte l'historique des
                  etapes, les pieces jointes et les actions. Zoho reste a cote
                  tant que la bascule n'a pas eu lieu. */}
              <button className="btn btn-secondary"
                onClick={() => window.open('/dossier/' + cr.dossier_id, '_blank', 'noopener')}>
                📁 Fiche dossier ↗
              </button>
              <button className="btn btn-secondary" onClick={() => window.open(`https://crmplus.zoho.eu/crm/tab/Potentials/${cr.dossier_id}`, '_blank')}>
                🔗 Zoho OT
              </button>
            </>
          )}
          {isAdmin && <button className="btn btn-secondary" onClick={() => setAction('edit')}>✏️ Modifier</button>}
          {isAdmin && isPending && (
            <>
              <button className="btn btn-danger" onClick={() => setAction('reject')}>✕ Rejeter</button>
              <button className="btn btn-success" onClick={() => setAction('validate-preview')}>✓ Valider et envoyer</button>
            </>
          )}
          {cr.status === 'validated' && (
            <button className="btn btn-secondary" onClick={async () => {
              try { await resendEmail(id); setMsg('Email renvoyé'); } catch(e) { setError('Erreur renvoi : ' + e.message); }
            }}>✉ Renvoyer</button>
          )}
          {!isPending && isAdmin && (
            <button className="btn btn-secondary" onClick={async () => {
              if (!window.confirm('Remettre ce CR en attente ?')) return;
              try { await resetCRPending(id); setCR(c => ({ ...c, status: 'pending' })); setMsg('CR remis en attente'); }
              catch(e) { setError('Erreur : ' + (e.response?.data?.error || e.message)); }
            }}>↩ En attente</button>
          )}
          {!isPending && (
            <span className={`badge ${cr.status === 'validated' ? 'badge-green' : 'badge-red'}`} style={{ fontSize: 13, padding: '6px 14px' }}>
              {cr.status === 'validated' ? '✓ Validé' : '✕ Rejeté'}
            </span>
          )}
        </div>
      </div>

      <div className="page">
        {msg && <div className="alert alert-success" onClick={() => setMsg('')}>{msg} ✕</div>}
        {error && <div className="alert alert-error" onClick={() => setError('')}>{error} ✕</div>}

        {/* ── Résumé ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { lbl: 'Résultat', val: cr.resultat || '—', color: typeColor },
            { lbl: 'Type CR', val: cr.type_cr || 'CRI', color: 'var(--blue2)' },
            { lbl: 'Technicien', val: cr.technicien_nom, color: 'var(--text)' },
            { lbl: 'Client final', val: cr.client_final || zoho.Client_final || zoho.Deal_Name || '—', color: 'var(--text)' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div style={{ fontSize: 15, fontWeight: 700, color: s.color, marginBottom: 4 }}>{s.val}</div>
              <div className="lbl">{s.lbl}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* ── Informations intervention ── */}
          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 14 }}>Informations intervention</div>
            <Row label="Référence commande" value={cr.ref_commande} />
            <Row label="Référence Work Order" value={cr.ref_work_order} />
            <Row label="Date arrivée" value={cr.date_arrivee ? new Date(cr.date_arrivee).toLocaleString('fr-FR') : null} />
            <Row label="Date fin" value={cr.date_fin ? new Date(cr.date_fin).toLocaleString('fr-FR') : null} />
            <Row label="Type raccordement" value={cr.type_raccordement} />
            <Row label="Usage PTO" value={cr.usage_pto} />
            <Row label="Réf PTO modifiée" value={cr.ref_pto_modifiee} />
            <Row label="N° série ONT" value={cr.numero_serie_ont} />
            <Row label="Contact sur site" value={cr.nom_contact} />
            <Row label="N° décharge KOSC" value={cr.decharge_kosc || cr.no_decharge} />
            <Row label="Longueur domaine public" value={cr.lg_public ? `${cr.lg_public} ml` : null} />
            <Row label="Longueur domaine privé" value={cr.lg_prive ? `${cr.lg_prive} ml` : null} />
            <Row label="Longueur totale cablage" value={cr.lg_total ? `${cr.lg_total} ml` : null} />
            <Row label="Longueur faux plafond" value={cr.lg_faux_plafond ? `${cr.lg_faux_plafond} ml` : null} />
            <Row label="Longueur goulotte" value={cr.lg_goulotte ? `${cr.lg_goulotte} ml` : null} />
            <Row label="Percement(s) complexe(s)" value={cr.percement ? (cr.nb_percemnets_complexes ? `Oui (${cr.nb_percemnets_complexes})` : 'Oui') : null} />
            <Row label="FPV (forfait plus-value)" value={cr.fpv ? 'Oui' : null} />
            <Row label="Travaux grande hauteur / Nacelle" value={cr.trav_grande_hauteur ? 'Oui' : null} />
            <Row label="Hydrocurage / débouchage" value={cr.hydrocurage ? 'Oui' : null} />
            <Row label="Pose de goulotte" value={cr.goulotte ? 'Oui' : null} />
            <Row label="Nacelle" value={cr.nacelle ? 'Oui' : null} />
            <Row label="HNO (intervention de nuit)" value={cr.hno ? 'Oui' : null} />
            <Row label="Binôme nécessaire" value={cr.binome ? 'Oui' : null} />
            <Row label="Nombre d'heures nécessaires" value={cr.duree_estimee ? `${cr.duree_estimee}h` : null} />
            <Row label="Forfait TVX (email)" value={forfaitDefault} />
            <Row label="N° VIA" value={zoho.N_VIA} />
            <Row label="Prise existante" value={zoho.Prise_existante} />

            <CommentBox label="Commentaire lié à l'intervention" value={cr.commentaire_intervention || cr.commentaire_technicien || cr.commentaire_intervenant} />
          </div>

          {/* ── PDF + Signatures + Rejet ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Documents</div>
              {cr.pdf_url ? (
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={async () => {
                    try {
                      const filename = cr.pdf_url.split('/').pop().split('?')[0];
                      const res = await fetch(`/api/cr/pdf/${filename}?v=${Date.now()}`, { headers: { Authorization: `Bearer ${token()}` } });
                      if (!res.ok) throw new Error();
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
                    } catch { alert('Erreur téléchargement'); }
                  }}>📄 Télécharger le PDF</button>
              ) : <div style={{ color: 'var(--text3)', fontSize: 13 }}>PDF non disponible</div>}

              {isAdmin && (
                <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                  onClick={async () => {
                    try { const result = await regeneratePDF(id); setCR(c => ({ ...c, pdf_url: result.pdf_url })); setMsg('PDF régénéré'); }
                    catch(e) { setError('Erreur régénération : ' + (e.response?.data?.error || e.message)); }
                  }}>🔄 Régénérer le PDF</button>
              )}

              {cr.devis_xlsx_url && (
                <button className="btn" style={{ width: '100%', justifyContent: 'center', background: '#1D6F42', color: '#fff', marginTop: 8 }}
                  onClick={async () => {
                    try {
                      const filename = cr.devis_xlsx_filename || cr.devis_xlsx_url.split('/').pop();
                      const res = await fetch(`/api/cr/devis/${filename}`, { headers: { Authorization: `Bearer ${token()}` } });
                      const blob = await res.blob(); const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
                    } catch { alert('Erreur téléchargement devis'); }
                  }}>📊 Devis XLSX</button>
              )}
              {cr.dostec_gc_url && (
                <button className="btn" style={{ width: '100%', justifyContent: 'center', background: '#7C3AED', color: '#fff', marginTop: 8 }}
                  onClick={async () => {
                    try {
                      const filename = cr.dostec_gc_filename || cr.dostec_gc_url.split('/').pop();
                      const res = await fetch(`/api/cr/dostec-gc/${filename}`, { headers: { Authorization: `Bearer ${token()}` } });
                      const blob = await res.blob(); const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
                    } catch { alert('Erreur téléchargement DOSTEC GC D15'); }
                  }}>📋 DOSTEC GC D15</button>
              )}
              {cr.dostec_gc_d14_url && (
                <button className="btn" style={{ width: '100%', justifyContent: 'center', background: '#B45309', color: '#fff', marginTop: 8 }}
                  onClick={async () => {
                    try {
                      const filename = cr.dostec_gc_d14_filename || cr.dostec_gc_d14_url.split('/').pop();
                      const res = await fetch(`/api/cr/dostec-gc/${filename}`, { headers: { Authorization: `Bearer ${token()}` } });
                      const blob = await res.blob(); const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
                    } catch { alert('Erreur téléchargement DOSTEC GC D14'); }
                  }}>📋 DOSTEC GC D14</button>
              )}
            </div>

            {/* Signature client */}
            {cr.signature_site && (
              <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 10 }}>Signature client</div>
                <img src={cr.signature_site} alt="signature" style={{ maxHeight: 80, background: '#fff', borderRadius: 8, padding: 8 }} />
                {cr.nom_contact && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{cr.nom_contact}</div>}
              </div>
            )}

            {/* Signature percement */}
            {cr.signature_percement && (
              <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 10 }}>Signature autorisation percement</div>
                <img src={cr.signature_percement} alt="signature percement" style={{ maxHeight: 80, background: '#fff', borderRadius: 8, padding: 8 }} />
                {cr.nom_signataire_percement && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{cr.nom_signataire_percement}</div>}
              </div>
            )}

            {/* Motif rejet */}
            {cr.status === 'rejected' && cr.motif_rejet && (
              <div className="card" style={{ border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.05)' }}>
                <div style={{ fontWeight: 700, marginBottom: 10, color: '#ef4444' }}>✕ Motif du rejet</div>
                <div style={{ background: 'rgba(239,68,68,0.08)', borderRadius: 8, padding: '10px 14px', fontSize: 13, lineHeight: 1.6 }}>{cr.motif_rejet}</div>
                {cr.rejected_at && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>Rejeté le {new Date(cr.rejected_at).toLocaleString('fr-FR')}</div>}
              </div>
            )}
          </div>
        </div>

        {/* ── Section SAV ── */}
        {cr.type_cr === 'SAV' && (
          <div className="card" style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 14 }}>Détails SAV</div>
            <Row label="N° ticket SAV" value={(cr.zoho_record || {}).Ref_Order_SAV} />
            <Row label="Typo raccordement" value={(cr.zoho_record || {}).Typo_raccordement} />
            <Row label="Types de réparation" value={Array.isArray(cr.types_reparation) ? cr.types_reparation.join(', ') : cr.types_reparation} />
            <Row label="Constructeur ONT" value={cr.constructeur_ont} />
            <Row label="N° série ONT (SAV)" value={cr.numero_serie_ont} />
            <Row label="Cause de l'échec" value={Array.isArray(cr.type_echec_sav) ? cr.type_echec_sav.join(', ') : cr.type_echec_sav} />
            <Row label="Devis à réaliser" value={cr.devis ? 'Oui' : null} />
            {cr.devis && <>
              <Row label="Type raccordement (devis)" value={cr.type_raccordement_sav} />
              <Row label="Longueur câble public" value={cr.lg_public ? `${cr.lg_public} ml` : null} />
              <Row label="Longueur câble privé" value={cr.lg_prive ? `${cr.lg_prive} ml` : null} />
              <Row label="Longueur faux plafond" value={cr.lg_faux_plafond ? `${cr.lg_faux_plafond} ml` : null} />
              <Row label="FPV (forfait plus-value)" value={cr.fpv ? 'Oui' : null} />
              <Row label="Nacelle / Travaux grande hauteur" value={cr.trav_grande_hauteur ? 'Oui' : null} />
              <Row label="HNO" value={cr.hno ? 'Oui' : null} />
              <Row label="Binôme" value={cr.binome ? 'Oui' : null} />
              <Row label="Percement complexe" value={cr.percement_complexe ? 'Oui' : null} />
              <Row label="Durée estimée" value={cr.duree_estimee ? `${cr.duree_estimee}h` : null} />
            </>}
            <CommentBox label="Commentaire technicien" value={cr.commentaire_technicien} />
          </div>
        )}

        {/* ── Section FAILED ── */}
        {isFailed && isCRI && (
          <div className="card" style={{ marginTop: 20, border: '1px solid rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.03)' }}>
            <div style={{ fontWeight: 700, marginBottom: 14, color: '#ef4444' }}>Détails de l'échec</div>
            <Row label="Type d'échec" value={Array.isArray(cr.type_echec_sav) ? cr.type_echec_sav.join(', ') : (cr.type_echec || null)} />
            <Row label="Précision type d'échec" value={cr.type_echec_autre} />

            {cr.report_rdv && cr.report_rdv.date && (
              <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 10, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.35)' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  Report de rendez-vous — demande {cr.report_rdv.demandeur || 'client'}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>
                  {cr.report_rdv.date.split('-').reverse().join('/')}
                  {cr.report_rdv.heure ? ' à ' + cr.report_rdv.heure : ''}
                </div>
                {cr.report_applique ? (
                  <div style={{ fontSize: 13, opacity: 0.8 }}>✓ Intervention déjà replanifiée à cette date.</div>
                ) : isPending ? (
                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                    Validez d'abord le compte rendu. La validation repasse le dossier
                    en « FAILED » dans Zoho : replanifier avant serait écrasé.
                  </div>
                ) : isAdmin ? (
                  <>
                    <button className="btn btn-primary" disabled={replanif} onClick={appliquerReport}>
                      {replanif ? 'Replanification…' : '📅 Replanifier à cette date'}
                    </button>
                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                      Repasse le dossier en « Planifiée » dans Zoho, à cette date et cette heure.
                      Le technicien affecté reste le même.
                    </div>
                  </>
                ) : null}
              </div>
            )}

            {/* Blocage GC */}
            {cr.type_echec === 'Blocage GC (entre deux chambres)' && <>
              <Row label="Distance Chambre A → Point bloquant" value={cr.dist_a_bloquant ? `${cr.dist_a_bloquant} ml` : null} />
              <Row label="Distance Chambre B → Point bloquant" value={cr.dist_b_bloquant ? `${cr.dist_b_bloquant} ml` : null} />
              <Row label="Nombre de conduites" value={cr.nb_conduites} />
              <Row label="Conduites cassées" value={cr.nb_conduites_cassees} />
              <Row label="Conduites saturées" value={cr.nb_conduites_saturees} />
              <Row label="GPS Chambre A" value={cr.gps_chambre_a} />
              <Row label="GPS Chambre B" value={cr.gps_chambre_b} />
              <Row label="GPS Point bloquant" value={cr.gps_point_bloquant} />
              <Row label="Emplacement point bloquant" value={cr.emplacement_point_bloquant} />
              <CommentBox label="Commentaire du blocage" value={cr.commentaire_blockage} />
            </>}

            {/* Chambre inaccessible */}
            {cr.type_echec === 'Chambre télécom inaccessible (tampon scellé)' && <>
              <Row label="GPS chambre" value={cr.gps_chambre_inaccessible} />
            </>}

            {/* Intervention sur DEVIS */}
            {cr.type_echec === 'Intervention sur DEVIS' && <>
              <Row label="Type de site" value={cr.type_site} />
              <Row label="Travaux grande hauteur / Nacelle" value={cr.trav_grande_hauteur ? 'Oui' : null} />
              <Row label="Pose de goulotte" value={cr.goulotte_devis || cr.goulotte ? 'Oui' : null} />
              <Row label="Longueur goulotte" value={cr.lg_goulotte ? `${cr.lg_goulotte} ml` : null} />
              <Row label="Hydrocurage / débouchage" value={cr.hydrocurage_devis ? 'Oui' : null} />
              <Row label="Binôme nécessaire" value={cr.binome ? 'Oui' : null} />
              <Row label="Intervention HNO" value={cr.hno ? 'Oui' : null} />
              <Row label="Longueur faux plafond devis" value={cr.lg_faux_plafond_devis ? `${cr.lg_faux_plafond_devis} ml` : null} />
              <Row label="Durée estimée" value={cr.duree_estimee ? `${cr.duree_estimee}h` : null} />
              <CommentBox label="Description détaillée du blocage" value={cr.desc_blockage} />
              <CommentBox label="Emplacement PBO détaillé" value={cr.emplacement_pbo_detail} />
            </>}

            {/* Chambre sur voirie ARC */}
            {cr.type_echec === 'Chambre sur voirie besoin ARC' && <>
              <Row label="GPS chambre voirie" value={cr.gps_chambre_voirie} />
              <Row label="Adresse chambre" value={cr.adresse_chambre_voirie} />
              <CommentBox label="Commentaire" value={cr.commentaire_chambre_voirie} />
            </>}

            {/* ARC (partagé) */}
            {cr.besoin_arc && <>
              <Row label="Besoin ARC" value="Oui" />
              <Row label="GPS chambre ARC" value={cr.gps_arc} />
              <Row label="Adresse chambre ARC" value={cr.adresse_arc} />
            </>}

            {/* Description générique pour les autres types */}
            {!['Blocage GC (entre deux chambres)', 'Intervention sur DEVIS', 'Chambre sur voirie besoin ARC', 'Chambre télécom inaccessible (tampon scellé)'].includes(cr.type_echec) && (
              <CommentBox label="Description de l'échec" value={cr.desc_blockage} />
            )}

            {/* Commentaire annulation client */}
            {cr.type_echec === 'Commande annulée par le client' && (
              <CommentBox label="Raison de l'annulation" value={cr.raison_annulation_client} />
            )}
          </div>
        )}

        {/* ── Photos — toutes les sections ── */}
        <div style={{ marginTop: 4 }}>
          {/* Commun */}
          <PhotoGrid photos={cr.photos_facade} label="Photo façade du site" />

          {/* SAV CLEARED */}
          <PhotoGrid photos={cr.photos_avant_panne} label="Photos avant intervention (état de panne)" />
          <PhotoGrid photos={cr.photo_pto} label="Photo PTO" />
          <PhotoGrid photos={cr.photo_valeur_pto} label="Photo valeur photomètre PTO" />
          <PhotoGrid photos={cr.photos_apres_voyant_ont} label="Photos après intervention — voyant ONT OK" />
          <PhotoGrid photos={cr.photos_autres} label="Photos autres" />

          {/* SAV FAILED */}
          <PhotoGrid photos={cr.photos_panne} label="Photos de la panne" />
          <PhotoGrid photos={cr.photos_cheminement_devis} label="Cheminement câble PTO → PBO (devis)" />

          {/* CRI CLEARED */}
          <PhotoGrid photos={cr.photo_ref_pto} label="Photo référence PTO" />
          <PhotoGrid photos={cr.photo_photometre_pto} label="Photo valeur photomètre PTO" />
          <PhotoGrid photos={cr.photo_pbo_avant} label="PBO — avant intervention" />
          <PhotoGrid photos={cr.photo_pbo_apres} label="PBO — après intervention" />
          <PhotoGrid photos={cr.photo_ref_fi_cable_pbo} label="Photo référence FI sur câble PBO" />
          <PhotoGrid photos={cr.photos_cheminement} label="Cheminement câble PBO → PTO" />
          <PhotoGrid photos={cr.photos_percemnets_complexes} label="Photos percement(s) complexe(s)" />
          <PhotoGrid photos={cr.photo_pm_avant} label="PM — avant intervention" />
          <PhotoGrid photos={cr.photo_pm_apres} label="PM — après intervention" />
          <PhotoGrid photos={cr.photo_photometre_pm} label="Photo valeur photomètre PM" />
          <PhotoGrid photos={cr.photo_ref_fi_jarr_pm} label="Photo référence FI sur jarretière au PM" />
          <PhotoGrid photos={cr.photo_etiquette_ont} label="Étiquette ONT" />
          <PhotoGrid photos={cr.photo_voyant_ont} label="Voyant ONT" />
          <PhotoGrid photos={cr.photo_test_ppp} label="Photo test PPP (PC)" />
          <PhotoGrid photos={cr.photo_test_activation} label="Photo test activation FTTH" />

          {/* FAILED — Blocage GC */}
          <PhotoGrid photos={cr.photo_chambre_a_env} label="Chambre A — environnement" />
          <PhotoGrid photos={cr.photo_chambre_a_ouverte} label="Chambre A — ouverte avec aiguille" />
          <PhotoGrid photos={cr.photo_chambre_b_env} label="Chambre B — environnement" />
          <PhotoGrid photos={cr.photo_chambre_b_ouverte} label="Chambre B — ouverte avec aiguille" />
          <PhotoGrid photos={cr.photo_point_bloquant} label="Point bloquant" />

          {/* FAILED — Chambre inaccessible */}
          <PhotoGrid photos={cr.photo_chambre_inaccessible_env} label="Chambre inaccessible — environnement" />
          <PhotoGrid photos={cr.photos_chambre_inaccessible} label="Chambre inaccessible — vue du dessus" />

          {/* FAILED — Devis */}
          <PhotoGrid photos={cr.photos_cheminement_failed} label="Photos cheminement à prévoir" />
          <PhotoGrid photos={cr.photo_pbo_devis} label="Photo PBO (devis)" />

          {/* FAILED — Voirie ARC */}
          <PhotoGrid photos={cr.photo_chambre_voirie_env} label="Chambre voirie — environnement" />
          <PhotoGrid photos={cr.photos_chambre_voirie_dessus} label="Chambre voirie — vue du dessus" />

          {/* FAILED — ARC partagé */}
          <PhotoGrid photos={cr.photos_arc} label="Photos chambre ARC" />

          {/* FAILED — Autres */}
          <PhotoGrid photos={cr.photos_echec} label="Photos de l'échec" />
          <PhotoGrid photos={cr.photos_annulation_client} label="Photos annulation client" />

          {/* Divers */}
          <PhotoGrid photos={cr.photo_continuite_pbo} label="Photo continuité PBO" />
          <PhotoGrid photos={cr.photo_signal_pm} label="Photo signal PM" />
          <PhotoGrid photos={cr.photo_signal_coupleur_pm} label="Photo signal coupleur PM" />
          <PhotoGrid photos={cr.photos_adresse_client} label="Photos adresse client" />
          <PhotoGrid photos={cr.photo_pbo_sature} label="Photo PBO saturé" />
          <PhotoGrid photos={cr.photos_pbo_disponible} label="Photo PBO disponible" />
        </div>

        {/* ── Modals ── */}
        {action === 'validate-preview' && (
          <EmailPreviewModal
            crId={id}
            cr={cr}
            onClose={() => setAction(null)}
            onConfirm={async (forfait_override) => {
              await handleValidate(forfait_override);
            }}
          />
        )}

        {action === 'reject' && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAction(null)}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-title">Rejeter le compte-rendu</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setAction(null)}>✕</button>
              </div>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-group">
                  <label className="form-label">Motif du rejet (visible par le technicien)</label>
                  <textarea className="form-input" value={motifRejet} onChange={e => setMotifRejet(e.target.value)} placeholder="Ex: Photos manquantes, signature absente, numéro ONT incorrect..." rows={4} style={{ resize: 'vertical' }} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setAction(null)}>Annuler</button>
                <button className="btn btn-danger" onClick={handleReject} disabled={processing}>
                  {processing ? <span className="spinner" /> : 'Rejeter'}
                </button>
              </div>
            </div>
          </div>
        )}

        {action === 'edit' && (
          <EditModal cr={cr} onClose={() => setAction(null)} onSave={handleEdit} />
        )}
      </div>
    </>
  );
}
